/**
 * Note Preview — Floating popover that shows rendered markdown + images.
 * Replaces plain-text title tooltips on notes badges across all views.
 *
 * Usage:
 *   import { showNotePreview, hideNotePreview } from './note-preview.js';
 *   badge.addEventListener('mouseenter', () => showNotePreview(badge, node.notes, docId, dek));
 *   badge.addEventListener('mouseleave', hideNotePreview);
 */

import { prepareStaticHtml, renderAttachmentsInElement } from '../views/markdown/attachment.js';
import { renderMathInElement } from '../views/markdown/math.js';

// markdownToHtml renders through a headless TipTap editor — code-split out of
// the boot bundle. Loaded on the first hover; later previews are instant.
let markdownToHtml = null;
let helpersLoading = null;
function loadMarkdownToHtml() {
  helpersLoading ||= import('../views/markdown/helpers.js').then((m) => {
    markdownToHtml = m.markdownToHtml;
  });
  return helpersLoading;
}

let previewEl = null;
let hideTimeout = null;
let currentTarget = null;

/**
 * Show a note preview popover near the given anchor element.
 *
 * @param {HTMLElement} anchor - Element to position near
 * @param {string} notes - Raw markdown content
 * @param {string} [docId] - Document ID for resolving attachments
 * @param {CryptoKey} [dek] - Encryption key for resolving attachments
 */
export function showNotePreview(anchor, notes, docId, dek) {
  if (!notes || !notes.trim()) return;
  if (currentTarget === anchor && previewEl) return; // already showing

  // First hover ever: fetch the markdown renderer, then re-enter. If the
  // pointer moved on in the meantime, currentTarget was cleared and we drop it.
  if (!markdownToHtml) {
    currentTarget = anchor;
    loadMarkdownToHtml().then(() => {
      if (currentTarget === anchor) {
        currentTarget = null;
        showNotePreview(anchor, notes, docId, dek);
      }
    });
    return;
  }

  hideNotePreview();
  clearTimeout(hideTimeout);
  currentTarget = anchor;

  previewEl = document.createElement('div');
  previewEl.className = 'note-preview-popover';

  // Render markdown to HTML
  const html = markdownToHtml(notes);
  previewEl.innerHTML = prepareStaticHtml(html);

  // Render math
  renderMathInElement(previewEl);

  // Resolve attachment images (async)
  if (docId && dek) {
    renderAttachmentsInElement(previewEl, docId, dek);
  }

  // Keep popover alive when hovering over it
  previewEl.addEventListener('mouseenter', () => clearTimeout(hideTimeout));
  previewEl.addEventListener('mouseleave', () => scheduleHide());

  document.body.appendChild(previewEl);

  // Position near anchor
  requestAnimationFrame(() => {
    if (!previewEl) return;
    const anchorRect = anchor.getBoundingClientRect();
    const popRect = previewEl.getBoundingClientRect();

    let left = anchorRect.right + 8;
    let top = anchorRect.top;

    // If overflows right, show on left side
    if (left + popRect.width > window.innerWidth - 12) {
      left = anchorRect.left - popRect.width - 8;
    }
    // If overflows left, center below
    if (left < 12) {
      left = Math.max(12, anchorRect.left + (anchorRect.width - popRect.width) / 2);
      top = anchorRect.bottom + 8;
    }
    // If overflows bottom, shift up
    if (top + popRect.height > window.innerHeight - 12) {
      top = Math.max(12, window.innerHeight - popRect.height - 12);
    }

    previewEl.style.left = `${left}px`;
    previewEl.style.top = `${top}px`;
  });
}

/**
 * Schedule hiding the preview (with a small delay so user can hover onto it).
 */
function scheduleHide() {
  hideTimeout = setTimeout(hideNotePreview, 200);
}

/**
 * Hide the note preview popover.
 */
export function hideNotePreview() {
  clearTimeout(hideTimeout);
  if (previewEl) {
    previewEl.remove();
    previewEl = null;
  }
  currentTarget = null;
}

/**
 * Setup note preview on a badge element.
 * Removes any existing title tooltip and adds hover handlers.
 *
 * @param {HTMLElement} badge - The notes badge element
 * @param {string} notes - Raw markdown content
 * @param {string} [docId] - Document ID
 * @param {CryptoKey} [dek] - Encryption key
 */
export function setupNotePreviewOnBadge(badge, notes, docId, dek) {
  if (!badge || !notes) return;
  badge.removeAttribute('title'); // remove plain text tooltip
  badge.addEventListener('mouseenter', () => showNotePreview(badge, notes, docId, dek));
  badge.addEventListener('mouseleave', () => scheduleHide());
}
